import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { plain, countEncryptedLeaks } from "@/lib/plain";
import { cn, letterColor } from "@/lib/util";
import { RefreshCw, Loader2, ShieldCheck, ChevronDown, ChevronUp, Info } from "lucide-react";

/**
 * Enterprise-grade rating card that fuses six data-source dimensions.
 * Falls back gracefully while the initial compute is running.
 */
export function SecurityRatingCard({ clientId }) {
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/security-rating/${clientId}`);
      setRating(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el rating");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await api.post(`/security-rating/${clientId}/recompute`);
      setRating(r.data);
      toast.success(`Rating recalculado · ${r.data.overall_letter} (${r.data.overall_score}/100)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo recalcular");
    } finally { setRecomputing(false); }
  };

  if (loading && !rating) return (
    <div className="border border-border bg-card/60 p-8 font-mono text-sm text-muted-foreground"
         data-testid="security-rating-loading">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Calculando rating…
    </div>
  );
  if (!rating) return null;

  return (
    <div className="border border-border bg-card/70" data-testid="security-rating-card">
      {countEncryptedLeaks(rating) > 0 && (
        <div className="border-b border-sev-critical/50 bg-sev-critical/10 p-3 text-xs flex items-start gap-2"
             data-testid="security-rating-encryption-warning">
          <span className="font-mono uppercase tracking-widest text-sev-critical">! cifrado</span>
          <span>El backend está devolviendo datos cifrados (<code className="font-mono">enc:v1:…</code>).
          Revisa que <code className="font-mono">ENCRYPTION_KEY</code> esté configurada en el entorno del backend.</span>
        </div>
      )}
      <div className="p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <h2 className="font-display text-lg font-medium">Rating de Seguridad</h2>
          {rating.client_name && (
            <span className="font-mono text-[11px] text-muted-foreground ml-2"
                  data-testid="security-rating-client-name">
              · {plain(rating.client_name)}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
            v{rating.algorithm_version} · objetivo
          </span>
        </div>
        <div className="flex items-center gap-2">
          {rating.cached && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                  data-testid="rating-cache-badge">
              cache · último cálculo hace &lt;60m
            </span>
          )}
          <button onClick={recompute} disabled={recomputing}
                  className="border border-border px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:border-primary hover:text-primary disabled:opacity-50"
                  data-testid="security-rating-recompute-btn">
            {recomputing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Recalcular
          </button>
        </div>
      </div>

      <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-border">
        <div>
          <div className="overline mb-2">Puntuación general</div>
          <div className="flex items-baseline gap-6">
            <span className={cn("font-display font-bold leading-none", letterColor(rating.overall_letter))}
                  style={{ fontSize: "clamp(96px, 12vw, 160px)" }}
                  data-testid="security-rating-letter">
              {rating.overall_letter}
            </span>
            <div className="flex flex-col">
              <span className="font-display text-5xl font-medium" data-testid="security-rating-score">
                {rating.overall_score}<span className="text-muted-foreground text-2xl">/100</span>
              </span>
              <span className="font-mono text-xs text-muted-foreground mt-2">
                calculado {new Date(rating.computed_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-[240px]">
          <div className="overline">Escala</div>
          <div className="grid grid-cols-5 gap-1 font-mono text-xs">
            {["A","B","C","D","F"].map(l => (
              <div key={l} className={cn("border px-3 py-1.5 text-center",
                l === rating.overall_letter ? cn(letterColor(l), "border-current bg-white/5")
                                              : "border-border text-muted-foreground")}>{l}</div>
            ))}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-wider">
            A≥90 · B≥80 · C≥70 · D≥60 · F&lt;60
          </div>
        </div>
      </div>

      <div className="p-5 space-y-2" data-testid="security-rating-breakdown">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Desglose por categoría</div>
          <button onClick={() => setExpanded(x => !x)}
                  className="text-xs font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            {expanded ? <>Contraer <ChevronUp className="w-3 h-3"/></> : <>Ver detalle <ChevronDown className="w-3 h-3"/></>}
          </button>
        </div>
        {rating.categories.map(c => (
          <CategoryRow key={c.category} c={c} expanded={expanded} />
        ))}

        {rating.errors?.length > 0 && (
          <details className="mt-3 border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs">
            <summary className="cursor-pointer text-sev-medium font-mono uppercase tracking-widest text-[10px]">
              {rating.errors.length} advertencia{rating.errors.length === 1 ? "" : "s"} durante el cálculo
            </summary>
            <ul className="mt-2 space-y-1 font-mono text-[11px] text-muted-foreground">
              {rating.errors.map((e, i) => <li key={i}>[{e.category}] {e.message}</li>)}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ c, expanded }) {
  const barColor = c.score >= 90 ? "bg-sev-low"
                   : c.score >= 70 ? "bg-primary"
                   : c.score >= 60 ? "bg-sev-medium"
                   : "bg-sev-critical";
  return (
    <div className="border-b border-border/40 py-3" data-testid={`security-rating-cat-${c.category}`}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{c.label}</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            peso {(c.weight * 100).toFixed(0)}% · aporta {c.impact} pts
          </div>
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          <span className={cn("font-display font-bold text-2xl", letterColor(c.letter))}>{c.letter}</span>
          <span className="font-mono text-sm text-muted-foreground">{c.score}/100</span>
        </div>
      </div>
      <div className="h-1.5 bg-muted/30 relative">
        <div className={cn("absolute top-0 left-0 h-full transition-all", barColor)}
             style={{ width: `${c.score}%` }} />
      </div>
      {expanded && (
        <div className="mt-2 p-2 bg-background/40 border border-border/40 text-[11px] font-mono text-muted-foreground flex items-start gap-2">
          <Info className="w-3 h-3 mt-0.5 shrink-0 text-primary" />
          <span>{c.why}</span>
        </div>
      )}
    </div>
  );
}
